import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Users, UserPlus, Trash2, Award, FileText, ScrollText, FileSignature, Upload, X, Edit2 } from "lucide-react";
import { api, getUser } from "../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DESIGNATIONS = ["Volunteer", "Teacher", "Doctor", "Nurse", "Radiologist", "Lab Technician", "Pharmacology", "Accountant", "Field Officer", "Coordinator", "Director"];

const CERTS = [
  { id: "member_id", label: "Member ID Card", icon: FileText, color: "#FF9933" },
  { id: "volunteer", label: "Volunteer Certificate", icon: Award, color: "#138808" },
  { id: "appointment", label: "Appointment Letter", icon: ScrollText, color: "#000080" },
  { id: "experience", label: "Experience Certificate", icon: FileSignature, color: "#dc2626" },
];

export default function Members() {
  const nav = useNavigate();
  const user = getUser();
  const [members, setMembers] = useState([]);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", father_name: "", dob: "", mobile: "", email: "",
    address: "", designation: "Volunteer", joining_date: new Date().toISOString().slice(0, 10), notes: "",
  });

  useEffect(() => {
    if (!user || !["admin", "accountant"].includes(user.role)) { nav("/accounting/login"); return; }
    load();
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    try { const { data } = await api.get("/members"); setMembers(data); }
    catch { toast.error("Failed to load members"); }
  };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (editing) {
        await api.put(`/members/${editing.id}`, form);
        toast.success("Member updated");
      } else {
        const { data } = await api.post("/members", form);
        toast.success(`Member created: ${data.member_id}`);
      }
      setAdding(false); setEditing(null);
      setForm({ name: "", father_name: "", dob: "", mobile: "", email: "",
                address: "", designation: "Volunteer", joining_date: new Date().toISOString().slice(0, 10), notes: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const upload = async (memberId, kind, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("kind", kind);
    try {
      await api.post(`/members/${memberId}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${kind} uploaded`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };

  const generateCert = (memberId, certType) => {
    window.open(`${BACKEND_URL}/api/members/${memberId}/certificate?cert_type=${certType}&t=${Date.now()}`, "_blank");
    // Note: window.open won't carry auth; use form post via API instead
  };

  // Generate via authed POST and open the resulting blob
  const generateCertAuth = async (memberId, certType) => {
    try {
      const token = localStorage.getItem("ahcct_token");
      const resp = await fetch(`${BACKEND_URL}/api/members/${memberId}/certificate?cert_type=${certType}`, {
        method: "POST", headers: { "Authorization": `Bearer ${token}` },
      });
      if (!resp.ok) { const t = await resp.text(); toast.error("Cert failed: " + t.slice(0, 100)); return; }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      toast.success("Certificate generated");
    } catch (e) { toast.error("Failed"); }
  };

  const del = async (memberId) => {
    if (!window.confirm("Delete this member and all their certificates?")) return;
    try { await api.delete(`/members/${memberId}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  if (!user || !["admin", "accountant"].includes(user.role)) return null;

  return (
    <div className="fade-in max-w-7xl mx-auto px-4 md:px-6 py-10">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#FF9933] font-bold mb-1">Members & Certificates</div>
          <h1 className="serif text-3xl md:text-4xl font-semibold text-[#000080]">Team Directory</h1>
          <p className="text-sm text-[#737373] mt-1">{members.length} active member(s)</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm({ name: "", father_name: "", dob: "", mobile: "", email: "", address: "", designation: "Volunteer", joining_date: new Date().toISOString().slice(0, 10), notes: "" }); setAdding(true); }} className="bg-[#FF9933] hover:bg-[#E68A2E] text-white rounded-full" data-testid="member-add-btn">
          <UserPlus className="w-4 h-4 mr-2" /> Add Member
        </Button>
      </div>

      {members.length === 0 ? (
        <div className="bg-white border border-dashed border-[#eae5d9] rounded-2xl p-16 text-center">
          <Users className="w-10 h-10 text-[#FF9933] mx-auto mb-3" strokeWidth={1.3} />
          <div className="serif text-xl text-[#000080]">No members yet</div>
          <p className="text-sm text-[#737373] mt-1">Click "Add Member" to register the first one.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {members.map(m => (
            <div key={m.id} className="bg-white rounded-2xl border border-[#eae5d9] p-5 card-hover" data-testid={`member-${m.member_id}`}>
              <div className="flex items-start gap-4">
                {m.photo_url ? (
                  <img src={`${BACKEND_URL}${m.photo_url}`} alt={m.name} className="w-16 h-20 object-cover rounded-lg border border-[#eae5d9]" />
                ) : (
                  <div className="w-16 h-20 bg-[#F3EFE7] rounded-lg flex items-center justify-center"><Users className="w-6 h-6 text-[#737373]" /></div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[11px] text-[#FF9933] font-semibold">{m.member_id}</div>
                  <div className="serif text-lg font-semibold text-[#000080] leading-tight">{m.name}</div>
                  <div className="text-xs text-[#737373]">{m.designation} · joined {m.joining_date}</div>
                  <div className="text-xs text-[#4A4A4A] mt-1">📱 {m.mobile}</div>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2">
                <label className="text-[11px] cursor-pointer">
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(m.id, "photo", e.target.files[0])} data-testid={`upload-photo-${m.member_id}`} />
                  <div className="text-xs text-center py-1.5 rounded-lg border border-[#eae5d9] hover:border-[#FF9933] flex items-center justify-center gap-1"><Upload className="w-3 h-3" /> Photo</div>
                </label>
                <label className="text-[11px] cursor-pointer">
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(m.id, "signature", e.target.files[0])} data-testid={`upload-sig-${m.member_id}`} />
                  <div className="text-xs text-center py-1.5 rounded-lg border border-[#eae5d9] hover:border-[#FF9933] flex items-center justify-center gap-1"><Upload className="w-3 h-3" /> Signature</div>
                </label>
              </div>

              <div className="mt-3">
                <div className="text-[11px] uppercase tracking-wider text-[#737373] font-bold mb-1.5">Generate Certificate</div>
                <div className="grid grid-cols-2 gap-1.5">
                  {CERTS.map(c => (
                    <button key={c.id} onClick={() => generateCertAuth(m.id, c.id)} data-testid={`cert-${c.id}-${m.member_id}`}
                      className="text-[11px] py-1.5 rounded-lg border border-[#eae5d9] hover:bg-[#FF9933]/5 hover:border-[#FF9933] flex items-center gap-1.5 px-2 text-left">
                      <c.icon className="w-3 h-3" style={{ color: c.color }} /> {c.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-3 pt-3 border-t border-[#eae5d9] flex justify-between text-xs">
                <button onClick={() => { setEditing(m); setForm({ ...m, dob: m.dob || "", email: m.email || "" }); setAdding(true); }} className="text-[#000080] flex items-center gap-1 font-semibold"><Edit2 className="w-3 h-3" /> Edit</button>
                <a href={`/verify/${m.member_id}`} target="_blank" rel="noreferrer" className="text-[#138808] font-semibold">Verify Public →</a>
                <button onClick={() => del(m.id)} className="text-red-500 flex items-center gap-1 font-semibold"><Trash2 className="w-3 h-3" /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={adding} onOpenChange={(v) => { setAdding(v); if (!v) setEditing(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="serif text-2xl text-[#000080]">{editing ? "Edit Member" : "Add Member"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="grid md:grid-cols-2 gap-3" data-testid="member-form">
            <Input required placeholder="Full Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="m-name" />
            <Input placeholder="Father / Guardian's Name" value={form.father_name} onChange={(e) => setForm({ ...form, father_name: e.target.value })} data-testid="m-father" />
            <Input type="date" placeholder="Date of Birth" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} data-testid="m-dob" />
            <Input required placeholder="Mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="m-mobile" />
            <Input type="email" placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="m-email" />
            <Select value={form.designation} onValueChange={(v) => setForm({ ...form, designation: v })}>
              <SelectTrigger data-testid="m-designation"><SelectValue /></SelectTrigger>
              <SelectContent>{DESIGNATIONS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
            <Input required type="date" placeholder="Joining Date" value={form.joining_date} onChange={(e) => setForm({ ...form, joining_date: e.target.value })} data-testid="m-joining" />
            <Textarea placeholder="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="md:col-span-2 min-h-[60px]" data-testid="m-address" />
            <Textarea placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="md:col-span-2 min-h-[50px]" data-testid="m-notes" />
            <div className="md:col-span-2 flex justify-end gap-3 mt-2">
              <Button type="button" variant="outline" onClick={() => { setAdding(false); setEditing(null); }} className="rounded-full">Cancel</Button>
              <Button type="submit" disabled={busy} className="bg-[#FF9933] hover:bg-[#E68A2E] text-white rounded-full" data-testid="m-submit">{busy ? "Saving..." : editing ? "Update" : "Create Member"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
